import {
  ANATOMICAL_ZONES,
  BODY_CENTER_X,
  CENTER_TOLERANCE,
  FIGURE_LAYOUT,
  LIMB_CENTER_EXCLUSION,
} from '../config/zones.config';

export function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

export function getSvgPointFromClient(svgElement, clientX, clientY) {
  if (!svgElement) return null;

  const point = svgElement.createSVGPoint();
  point.x = clientX;
  point.y = clientY;
  const ctm = svgElement.getScreenCTM();
  if (!ctm) return null;
  return point.matrixTransform(ctm.inverse());
}

export function normalizeSvgPoint(point, viewBox) {
  if (!point || !viewBox) return null;
  return {
    x: clamp(((point.x - viewBox.x) / viewBox.width) * 100, 0, 100),
    y: clamp(((point.y - viewBox.y) / viewBox.height) * 100, 0, 100),
  };
}

/** Map raw SVG coordinates to 0–100 figure space aligned with the body image. */
export function toFigureCoordinates(svgPoint) {
  if (!svgPoint) return null;
  return {
    x: clamp(((svgPoint.x - FIGURE_LAYOUT.x) / FIGURE_LAYOUT.width) * 100, 0, 100),
    y: clamp(((svgPoint.y - FIGURE_LAYOUT.y) / FIGURE_LAYOUT.height) * 100, 0, 100),
  };
}

/** Map figure-space coordinates back to SVG viewBox for rendering overlays. */
export function fromFigureCoordinates(figurePoint) {
  if (!figurePoint) return null;
  return {
    x: FIGURE_LAYOUT.x + (figurePoint.x / 100) * FIGURE_LAYOUT.width,
    y: FIGURE_LAYOUT.y + (figurePoint.y / 100) * FIGURE_LAYOUT.height,
  };
}

export function calculateCentroid(points) {
  if (!points?.length) return null;
  const sum = points.reduce(
    (acc, next) => ({ x: acc.x + next.x, y: acc.y + next.y }),
    { x: 0, y: 0 }
  );
  return {
    x: sum.x / points.length,
    y: sum.y / points.length,
  };
}

/** Convert figure-space circle to SVG viewBox for overlay rendering. */
export function toViewBoxCircle(circle) {
  if (!circle) return null;
  const center = fromFigureCoordinates({ x: circle.cx, y: circle.cy });
  if (!center) return null;
  const scale = FIGURE_LAYOUT.width / 100;
  return {
    cx: center.x,
    cy: center.y,
    r: circle.r * scale,
  };
}

/** Convert figure-space point to SVG viewBox for overlay rendering. */
export function toViewBoxPoint(figurePoint) {
  return fromFigureCoordinates(figurePoint);
}

export function getDistance(a, b) {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

const LIMB_ZONE_IDS = new Set(['upper_arm', 'forearm', 'hand']);

function pointInBounds(point, bounds) {
  return (
    point.x >= bounds.xMin &&
    point.x <= bounds.xMax &&
    point.y >= bounds.yMin &&
    point.y <= bounds.yMax
  );
}

function isInCenterCorridor(x) {
  return Math.abs(x - BODY_CENTER_X) <= LIMB_CENTER_EXCLUSION;
}

function effectiveBounds(zone, focal) {
  if (!zone.lateral || !focal) return zone.bounds;

  if (focal.x < BODY_CENTER_X - CENTER_TOLERANCE) {
    return {
      ...zone.bounds,
      xMax: Math.min(zone.bounds.xMax, BODY_CENTER_X - 2),
    };
  }
  if (focal.x > BODY_CENTER_X + CENTER_TOLERANCE) {
    return {
      ...zone.bounds,
      xMin: Math.max(zone.bounds.xMin, BODY_CENTER_X + 2),
    };
  }
  return zone.bounds;
}

function circleIntersectsBounds(circle, bounds) {
  if (!circle) return false;
  const nearestX = clamp(circle.cx, bounds.xMin, bounds.xMax);
  const nearestY = clamp(circle.cy, bounds.yMin, bounds.yMax);
  const dx = circle.cx - nearestX;
  const dy = circle.cy - nearestY;
  return dx * dx + dy * dy <= circle.r * circle.r;
}

function estimateCircleBoundsCoverage(circle, bounds) {
  if (!circle || circle.r <= 0) return 0;
  const sampleCount = 20;
  let inside = 0;
  let total = 0;
  for (let i = 0; i < sampleCount; i += 1) {
    const angle = (2 * Math.PI * i) / sampleCount;
    const x = circle.cx + Math.cos(angle) * circle.r;
    const y = circle.cy + Math.sin(angle) * circle.r;
    total += 1;
    if (x >= bounds.xMin && x <= bounds.xMax && y >= bounds.yMin && y <= bounds.yMax) {
      inside += 1;
    }
  }
  return total > 0 ? inside / total : 0;
}

function zoneCenter(bounds) {
  return {
    x: (bounds.xMin + bounds.xMax) / 2,
    y: (bounds.yMin + bounds.yMax) / 2,
  };
}

function verticalBandForY(y, x = null) {
  if (x != null && !isInCenterCorridor(x)) {
    if (y >= 20 && y < 40) return 'upper_arm';
    if (y >= 40 && y < 56) return 'forearm';
    if (y >= 56 && y < 69) return 'hand';
  }
  if (y < 16) return 'head_neck';
  if (y < 36) return 'upper_trunk';
  if (y < 51) return 'lower_trunk';
  if (y < 63) return 'pelvis_hips';
  if (y < 77) return 'thigh';
  if (y < 84) return 'knee';
  if (y < 93) return 'calf';
  return 'foot';
}

function zoneSubregion(zoneId, yRatio) {
  const r = clamp(yRatio, 0, 1);
  if (zoneId === 'calf') {
    if (r < 0.34) return 'upper lower leg (just below the knee)';
    if (r < 0.67) return 'middle lower leg';
    return 'lower lower leg (toward the ankle)';
  }
  if (zoneId === 'knee') {
    if (r < 0.4) return 'upper knee area';
    if (r > 0.6) return 'lower knee area';
    return 'center of the knee';
  }
  if (zoneId === 'thigh') {
    if (r < 0.33) return 'upper thigh';
    if (r < 0.66) return 'mid thigh';
    return 'lower thigh';
  }
  if (zoneId === 'upper_arm' || zoneId === 'forearm') {
    if (r < 0.33) return `upper ${zoneId === 'upper_arm' ? 'upper arm' : 'forearm'}`;
    if (r < 0.66) return `mid ${zoneId === 'upper_arm' ? 'upper arm' : 'forearm'}`;
    return `lower ${zoneId === 'upper_arm' ? 'upper arm' : 'forearm'}`;
  }
  if (zoneId === 'upper_trunk') {
    if (r < 0.35) return 'upper chest / upper back';
    if (r > 0.65) return 'lower chest / mid back';
    return 'mid chest / mid back';
  }
  if (zoneId === 'lower_trunk') {
    if (r < 0.4) return 'upper abdomen / upper lumbar area';
    if (r > 0.6) return 'lower abdomen / lower lumbar area';
    return 'mid abdomen / mid lumbar area';
  }
  return null;
}

function buildPreciseSummary({ zone, laterality, view, focal }) {
  if (!zone?.label || !focal) return null;
  const side = laterality && laterality !== 'central' ? `${laterality} ` : '';
  const surface = view === 'back' ? 'back' : 'front';
  const ySpan = Math.max(1, zone.bounds.yMax - zone.bounds.yMin);
  const yRatio = (focal.y - zone.bounds.yMin) / ySpan;
  const subregion = zoneSubregion(zone.id, yRatio);
  if (subregion) {
    return `${side}${subregion} on the ${surface}`.trim();
  }
  return `${side}${zone.label} on the ${surface}`.trim();
}

function scoreZoneMatch(zone, focal, points, circle) {
  const bounds = effectiveBounds(zone, focal);
  let score = 0;

  if (pointInBounds(focal, bounds)) score += 4;

  if (points?.length) {
    const containedPoints = points.filter((point) => pointInBounds(point, bounds)).length;
    score += (containedPoints / points.length) * 2;
  }

  if (circle && circleIntersectsBounds(circle, bounds)) {
    score += 1.25;
    score += estimateCircleBoundsCoverage(circle, bounds) * 2.25;
  }

  const center = zoneCenter(bounds);
  const proximity = Math.max(0, 1.8 - getDistance(focal, center) / 24);
  score += proximity;

  const expectedBand = verticalBandForY(focal.y, focal.x);
  if (zone.id === expectedBand) {
    score += 2.5;
  } else if (
    (zone.id === 'thigh' && expectedBand === 'knee') ||
    (zone.id === 'knee' && expectedBand === 'thigh') ||
    (zone.id === 'knee' && expectedBand === 'calf') ||
    (zone.id === 'calf' && expectedBand === 'knee')
  ) {
    score += 0.5;
  } else {
    score -= 1.25;
  }

  if (LIMB_ZONE_IDS.has(zone.id) && isInCenterCorridor(focal.x)) {
    score *= 0.15;
  }
  if (!zone.lateral && isInCenterCorridor(focal.x) && (zone.id === 'upper_trunk' || zone.id === 'lower_trunk')) {
    score += 1.5;
  }

  return score;
}

export function buildLocationSummary({ zone, laterality, view }) {
  if (!zone?.label) return null;
  const side = laterality && laterality !== 'central' ? `${laterality} ` : '';
  const surface = view === 'back' ? 'back' : 'front';
  return `${side}${zone.label} on the ${surface}`.trim();
}

function detectLaterality(point, points, zone) {
  if (!zone.lateral) return 'central';

  if (points?.length > 1) {
    const minX = Math.min(...points.map((entry) => entry.x));
    const maxX = Math.max(...points.map((entry) => entry.x));
    if (minX < BODY_CENTER_X - CENTER_TOLERANCE && maxX > BODY_CENTER_X + CENTER_TOLERANCE) {
      return 'bilateral';
    }
  }

  // Figure faces the patient (mirror view): screen-left = patient's right side.
  if (point.x < BODY_CENTER_X - CENTER_TOLERANCE) return 'right';
  if (point.x > BODY_CENTER_X + CENTER_TOLERANCE) return 'left';
  return 'central';
}

export function detectZoneFromSelection({ centroid, points, view, circle = null }) {
  const focal = centroid || calculateCentroid(points || []);
  if (!focal) return null;

  const candidates = ANATOMICAL_ZONES.filter((zone) => zone.views.includes(view));
  if (!candidates.length) return null;

  let bestZone = null;
  let bestScore = -1;
  candidates.forEach((zone) => {
    const score = scoreZoneMatch(zone, focal, points, circle);
    if (score > bestScore) {
      bestScore = score;
      bestZone = zone;
    }
  });
  if (!bestZone) return null;

  const laterality = detectLaterality(focal, points, bestZone);
  const preciseSummary = buildPreciseSummary({
    zone: bestZone,
    laterality,
    view,
    focal,
  });

  return {
    id: bestZone.id,
    label: bestZone.label,
    code: bestZone.codeByView?.[view] || bestZone.code || bestZone.id,
    laterality,
    confidence: Number(Math.min(1, Math.max(0, bestScore / 10)).toFixed(2)),
    summary: buildLocationSummary({ zone: bestZone, laterality, view }),
    precise_summary: preciseSummary,
    focal_point: { x: Number(focal.x.toFixed(2)), y: Number(focal.y.toFixed(2)) },
    bounds: bestZone.bounds,
    vertical_band: verticalBandForY(focal.y, focal.x),
  };
}
